/**
 * Iter 153 — 30-day × 24-hour Month Timeline
 *
 * Props: { wsId, month "YYYY-MM", brandFilter, onOpen(item) }
 *
 * Zoom modes:
 *   - "4h"   → 30 rows × 6 cols  (each cell = 4 hours)
 *   - "1h"   → 30 rows × 24 cols (each cell = 1 hour)
 *   - "15m"  → 30 rows × 96 cols (each cell = 15 minutes) — horizontally scrollable
 *
 * Drag a post block to any cell → PATCH /content-items/{id}/reschedule with
 * new date + time. HTML5 native drag-and-drop.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Clock, ZoomIn, ZoomOut } from "lucide-react";
import { CalendarHoverCard } from "@/pages/app/ContentCalendar";

const ZOOMS = {
  "4h": { cols: 6, slotMinutes: 240, colWidth: 60, label: "4h" },
  "1h": { cols: 24, slotMinutes: 60, colWidth: 34, label: "1h" },
  "15m": { cols: 96, slotMinutes: 15, colWidth: 16, label: "15m" },
};

function monthDays(month) {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m, 0).getDate();
}

function timeToSlot(planned_time, slotMinutes) {
  if (!planned_time || planned_time.length < 5) return 0;
  const [hh, mm] = planned_time.split(":").map(Number);
  const total = hh * 60 + mm;
  return Math.floor(total / slotMinutes);
}

function slotToTime(slotIdx, slotMinutes) {
  const total = slotIdx * slotMinutes;
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

export default function MonthTimeline({ wsId, month, brandFilter, onOpen }) {
  const [zoom, setZoom] = useState("1h");
  const [items, setItems] = useState(null);
  const [dragId, setDragId] = useState(null);
  const days = monthDays(month);
  const cfg = ZOOMS[zoom];

  const load = useCallback(async () => {
    if (!wsId || !month) return;
    try {
      const p = new URLSearchParams({ month });
      if (brandFilter && brandFilter !== "_all") p.set("brand_id", brandFilter);
      const r = await api.get(`/workspaces/${wsId}/timeline?${p.toString()}`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load timeline"));
      setItems([]);
    }
  }, [wsId, month, brandFilter]);
  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const m = new Map();
    (items || []).forEach((it) => {
      const d = parseInt((it.planned_date || "").slice(8), 10);
      if (!m.has(d)) m.set(d, []);
      m.get(d).push(it);
    });
    return m;
  }, [items]);

  const drop = async (day, slotIdx) => {
    if (!dragId) return;
    const it = (items || []).find((x) => x.id === dragId);
    if (!it) return;
    const time = slotToTime(slotIdx, cfg.slotMinutes);
    const [y, mo] = month.split("-").map(Number);
    const date = `${y}-${String(mo).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    try {
      await api.patch(`/content-items/${it.id}/reschedule`, {
        planned_date: date, planned_time: time,
      });
      toast.success(`Moved to ${date} · ${time}`);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Reschedule failed"));
    } finally {
      setDragId(null);
    }
  };

  if (items === null) return <div className="text-sm text-muted-foreground">Loading timeline…</div>;

  return (
    <div data-testid="month-timeline">
      {/* Zoom controls */}
      <div className="flex items-center gap-2 mb-3">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Zoom</div>
        {(["4h", "1h", "15m"]).map((z) => (
          <Button key={z} size="sm" variant={zoom === z ? "default" : "outline"}
                  className="rounded-none h-7 px-2 text-[11px]"
                  onClick={() => setZoom(z)} data-testid={`timeline-zoom-${z}`}>
            {z === "4h" && <ZoomOut className="w-3 h-3 mr-1" />}
            {z === "15m" && <ZoomIn className="w-3 h-3 mr-1" />}
            {z === "1h" && <Clock className="w-3 h-3 mr-1" />}
            {ZOOMS[z].label}
          </Button>
        ))}
        <span className="text-[10px] text-muted-foreground ml-auto">{items.length} posts · drag any block to reschedule</span>
      </div>

      {/* Grid */}
      <div className="border border-border overflow-auto" style={{ maxHeight: "70vh" }}>
        <table className="border-collapse" style={{ minWidth: cfg.cols * cfg.colWidth + 60 }}>
          <thead>
            <tr className="bg-muted/40 sticky top-0 z-10">
              <th className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 py-1 border-r border-border sticky left-0 bg-muted/40 z-20" style={{ width: 60 }}>Day</th>
              {Array.from({ length: cfg.cols }, (_, i) => (
                <th key={i} className="text-[9px] font-mono text-muted-foreground border-r border-border py-1 text-center"
                    style={{ minWidth: cfg.colWidth, width: cfg.colWidth }}>
                  {slotToTime(i, cfg.slotMinutes)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: days }, (_, di) => {
              const day = di + 1;
              const dayItems = byDay.get(day) || [];
              return (
                <tr key={day} className="border-b border-border">
                  <td className="text-[10px] font-mono text-muted-foreground px-2 border-r border-border sticky left-0 bg-background z-10 text-center" style={{ width: 60 }}>
                    {String(day).padStart(2, "0")}
                  </td>
                  {Array.from({ length: cfg.cols }, (_, ci) => {
                    const cellItems = dayItems.filter((it) => timeToSlot(it.planned_time, cfg.slotMinutes) === ci);
                    return (
                      <td key={ci}
                          className="border-r border-border align-top relative"
                          style={{ minWidth: cfg.colWidth, width: cfg.colWidth, height: 28 }}
                          onDragOver={(e) => { e.preventDefault(); }}
                          onDrop={() => drop(day, ci)}
                          data-testid={`timeline-cell-${day}-${ci}`}>
                        {cellItems.map((it) => (
                          <HoverCard key={it.id} openDelay={220} closeDelay={80}>
                            <HoverCardTrigger asChild>
                              <button
                                type="button"
                                draggable
                                onDragStart={() => setDragId(it.id)}
                                onDragEnd={() => setDragId(null)}
                                onClick={() => onOpen?.(it)}
                                title={`${it.name || it.topic} · ${it.brand_name} · ${it.planned_time || "no time"}${it.primary_assignee_name ? ` · owner: ${it.primary_assignee_name}` : ""}`}
                                className={`absolute inset-0.5 text-[9px] px-0.5 truncate text-left cursor-grab active:cursor-grabbing text-white shadow-sm ${dragId === it.id ? "opacity-50" : ""}`}
                                style={{
                                  background: it.status === "published" ? "#059669"
                                             : it.status === "cancelled" ? "#dc2626"
                                             : it.status === "scheduled" ? "#0891b2"
                                             : "#7c3aed",
                                }}
                                data-testid={`timeline-block-${it.id}`}
                              >
                                {it.name || it.topic}
                              </button>
                            </HoverCardTrigger>
                            <HoverCardContent side="right" align="start" className="w-96 p-0 rounded-none"
                                                data-testid={`timeline-hovercard-${it.id}`}>
                              <CalendarHoverCard it={it} onOpen={() => onOpen?.(it)} />
                            </HoverCardContent>
                          </HoverCard>
                        ))}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "#7c3aed" }} /> planned</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "#0891b2" }} /> scheduled</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "#059669" }} /> published</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "#dc2626" }} /> cancelled</span>
      </div>
    </div>
  );
}
